import React, { useState, ChangeEvent, FormEvent, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { startUpload, uploadSuccess, uploadFailure } from '../../store/imageSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchUserData } from '../../store/UserSlice';
import apiClient from '../../api/apiClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface PopupContentProps {
  closePopups: () => void;
}

const PopupContent: React.FC<PopupContentProps> = ({closePopups}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [title, setTitle] = useState('');
  const [image, setImage] = useState<File | null>(null);  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createConditionMutation = useMutation({
    mutationFn: (formData: FormData) => apiClient.post('/api/v1/healthConditions', formData),
    onSuccess: (response) => {
      dispatch(uploadSuccess(response.data.imageUrl));
      dispatch(fetchUserData()).then(() => {
        closePopups();
        toast.success(t("dashboard.addSuccess"));
      });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || t('common.error');
      dispatch(uploadFailure(msg));
      toast.error(msg);
    },
  });

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
        setImage(file);
        setError(null);
      } else {
        setSelectedImage(null);
        setImage(null);
        setError(t('dashboard.invalidImage'));
      }
    } else {
      setSelectedImage(null);
      setImage(null);
      setError(t('dashboard.invalidImage'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedImage) {
      setError(t('dashboard.invalidImage'));
      return;
    }
    dispatch(startUpload());
    const formData = new FormData();
    formData.append('icon', selectedImage);
    formData.append('name', title);
    createConditionMutation.mutate(formData);
  };

  return (
    <div className="max-w-md mt-2 m-auto p-4 md:p-8 bg-white rounded ">
      <h2 className="text-xl md:text-2xl flex font-gilroy mb-6 font-bold  text-[#263238]">
        <img src='./images/round_icon.png' alt="condition" className='object-contain pr-2'/>
        {t("dashboard.addNewCondition")}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-[#607D8B] text-sm mb-2 font-gilroy" htmlFor="image">
            {t("dashboard.addIcon")}
          </label>
          <div className="flex items-center  w-full">
          <label className={`flex flex-col items-center  w-32 h-30 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50  hover:bg-gray-100 ${selectedImage instanceof File ? "hidden" : ""}`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6 " >
                <img src='./images/gallery-add.png' alt="upload"  />
                <p className="text-xs text-[#4C535F] mt-4 font-gilroy">{t("dashboard.uploadIcon")}</p>
              </div>
              <input
                    id="image"
                    type="file"
                    name="image"
                    onChange={handleImageChange}
                    className={"visually-hidden"} // Corrected syntax
                    accept="image/*"
                    required  
                    ref={fileInputRef}
                  />  

            </label>
            {selectedImage instanceof File && (
              <img src={URL.createObjectURL(selectedImage)} alt="preview" className="w-auto max-w-md pl-2 h-32 object-contain" />
            )}

            {error && <p style={{ color: 'red' }}>{error}</p>}
          </div>
        </div>

        <div className="mb-6 mt-4">
          <label className="block text-[#607D8B] text-sm mb-2  font-gilroy" htmlFor="title">
            {t("dashboard.healthConditions")}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={handleTitleChange}
            className="w-full  font-gilroy px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 bg-[#F5F7F8]"
            placeholder={t("dashboard.enterTitle")}
            required
          />
        </div>    
        <button
          type="submit"
          className="bg-[#B4804A] font-gilroy w-full text-white py-2 px-4 font-semibold rounded-md"
        >
          {t("dashboard.addNewCondition")}
        </button>
      </form>
    </div>
  );
};

export default PopupContent;
